import PageHeader from "@/components/site/PageHeader";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import Reveal from "@/components/site/Reveal";
import { FEATURED_PROPERTY, PROPERTY_GALLERY } from "@/lib/constants";

const FEATURED = {
  location: "Ocean Isle Beach, NC",
  type: "4 Bedroom Coastal Vacation Rental",
  title: "Repositioning for Seasonal Demand",
  body: [
    "After revisiting the property's positioning, updating the listing presentation, and aligning the messaging with spring and summer traveler demand, the property experienced a significant increase in booking performance.",
    "Rather than focusing solely on property features, the listing was repositioned around the guest experience and seasonal booking intent.",
  ],
  results: [
    "Occupancy increased from approximately 40% to fully booked across the next booking window",
    "Listing repositioned around seasonal traveler demand and guest experience",
    "Approximately 2–3 hours per week reclaimed by the owner through guest communication and coordination support",
  ],
  img: FEATURED_PROPERTY,
};

const METRICS = [
  {
    label: "Bear Necessities Cabin",
    meta: "2 Bedroom Tiny Home · Franklin, GA",
    stats: [
      { v: "62%", k: "Occupancy" },
      { v: "1.41%", k: "Conversion Rate" },
      { v: "4.96", k: "Overall Rating" },
    ],
  },
  {
    label: "Cozy Cub Cabin",
    meta: "2 Bedroom Mountain Home · Clayton, GA",
    stats: [
      { v: "$48,000", k: "Annual Revenue" },
      { v: "98%", k: "Communication Rating" },
      { v: "58%", k: "Occupancy" },
    ],
  },
];

const GUEST_QUOTES = [
  {
    quote: "Sarah was a great host. Super easy and fast to communicate with.",
    name: "Claudia",
    location: "Clayton, GA",
  },
  {
    quote: "The welcome gifts were so nice. Cabin had everything we needed.",
    name: "Amanda",
    location: "Clayton, GA",
  },
  {
    quote: "The contact with management was smooth and they wanted to keep us happy.",
    name: "John",
    location: "Franklin, NC",
  },
  {
    quote: "Place is very nice. There were a couple of issues due to electrical problems, but they were resolved quickly. The contact with management was smooth and they wanted to keep us happy.",
    name: "Ernest",
    location: "Franklin, NC",
  },
];

export default function SuccessStories() {
  usePageTitle("Success Stories");
  return (
    <div data-testid="page-success-stories" className="bg-ivory">
      <PageHeader
        overline="Success Stories"
        title="Real Properties. Real Results."
        accent="Real Guest Experiences."
        description="Over the years, Sarah Atkinson has supported short-term rental owners through listing optimization, guest communication, operational support, positioning strategy, and performance improvement. Explore a selection of property results and guest experiences below."
        testId="success-header"
      />

      {/* Featured case study */}
      <section className="pb-24 lg:pb-28" data-testid="featured-case-study">
        <div className="container-editorial">
          <Reveal>
            <p className="overline mb-8">Featured Success Story</p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-7">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={FEATURED.img}
                    alt={`${FEATURED.location} — ${FEATURED.type}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="font-ui uppercase tracking-[0.18em] text-[12px] font-semibold text-charcoal/70 mt-5">
                  {FEATURED.location} · {FEATURED.type}
                </p>
              </div>

              <div className="lg:col-span-5 lg:pt-4">
                <p className="font-ui uppercase tracking-[0.18em] text-[13px] font-semibold text-sage mb-4">
                  Case Study
                </p>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.4rem] text-navy leading-[1.12]">
                  {FEATURED.title}
                </h2>
                <div className="mt-7 space-y-5">
                  {FEATURED.body.map((p, i) => (
                    <p key={i} className="font-sans text-[15.5px] text-charcoal/80 leading-[1.85]">
                      {p}
                    </p>
                  ))}
                </div>

                <div className="mt-10 border-t border-warmGray/30 pt-8">
                  <p className="font-ui uppercase tracking-[0.18em] text-[12px] font-semibold text-navy/80 mb-5">
                    Results
                  </p>
                  <ul className="space-y-3">
                    {FEATURED.results.map((r, i) => (
                      <li key={i} className="flex gap-3 font-sans text-[15px] text-charcoal/85 leading-[1.65]">
                        <Check className="w-4 h-4 text-sage shrink-0 mt-1" strokeWidth={1.8} />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Performance Highlights */}
      <section className="py-24 lg:py-28 bg-cream" data-testid="performance-highlights">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <p className="overline mb-5">Performance Highlights</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.5rem] text-navy leading-[1.12]">
                Measured outcomes across{" "}
                <em className="font-serif italic text-sage">supported properties.</em>
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {METRICS.map((m, i) => (
              <Reveal key={m.label} delay={i * 120}>
                <div
                  data-testid={`metric-card-${i}`}
                  className="bg-ivory border border-warmGray/20 p-10 lg:p-12 h-full"
                >
                  <span className="block w-10 h-px bg-gold mb-7" />
                  <h3 className="font-serif text-[1.55rem] lg:text-[1.75rem] text-navy leading-[1.18] mb-2">
                    {m.label}
                  </h3>
                  <p className="font-ui uppercase tracking-[0.16em] text-[12px] font-semibold text-charcoal/70 mb-10">
                    {m.meta}
                  </p>
                  <div className="grid grid-cols-3 gap-6 border-t border-warmGray/25 pt-8">
                    {m.stats.map((s, j) => (
                      <div key={j}>
                        <p className="font-serif text-2xl lg:text-[1.85rem] text-sage leading-tight">{s.v}</p>
                        <p className="font-ui uppercase tracking-[0.14em] text-[11px] font-semibold text-charcoal/70 mt-2 leading-snug">
                          {s.k}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Guest Experiences */}
      <section className="py-24 lg:py-32" data-testid="guest-experiences">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <p className="overline mb-5">Guest Experiences</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.5rem] text-navy leading-[1.12]">
                The small things guests{" "}
                <em className="font-serif italic text-sage">tend to remember.</em>
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {GUEST_QUOTES.map((g, i) => (
              <Reveal key={i} delay={(i % 2) * 100}>
                <figure
                  data-testid={`guest-quote-${i}`}
                  className="bg-cream border border-warmGray/20 p-9 lg:p-11 h-full flex flex-col"
                >
                  <span className="font-serif text-gold text-5xl leading-none mb-3 -ml-0.5">"</span>
                  <blockquote className="font-serif italic text-navy text-[1.15rem] md:text-[1.25rem] leading-[1.55] flex-1">
                    {g.quote}
                  </blockquote>
                  <figcaption className="mt-7 pt-6 border-t border-warmGray/25">
                    <p className="font-ui uppercase tracking-[0.16em] text-[12px] font-semibold text-navy">
                      — {g.name}
                    </p>
                    <p className="font-sans text-[14px] text-charcoal/65 mt-1.5">{g.location}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Property Gallery */}
      <section className="py-24 lg:py-28 bg-cream" data-testid="property-gallery">
        <div className="container-editorial">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <p className="overline mb-5">Property Gallery</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.4rem] text-navy leading-[1.12]">
                Properties Supported Through{" "}
                <em className="font-serif italic text-sage">iEssentials Property Management.</em>
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {PROPERTY_GALLERY.map((src, i) => (
              <Reveal key={i} delay={(i % 3) * 90}>
                <div
                  data-testid={`gallery-item-${i}`}
                  className="aspect-[4/3] overflow-hidden group"
                >
                  <img
                    src={src}
                    alt={`Supported property ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-ivory border-t border-warmGray/15" data-testid="success-cta">
        <div className="container-editorial">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-12 items-end gap-10 lg:gap-14 max-w-5xl mx-auto">
              <div className="md:col-span-8">
                <p className="overline mb-5">Begin</p>
                <h2 className="font-serif text-navy text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.18]">
                  Ready to find your{" "}
                  <em className="font-serif italic text-sage">revenue leaks?</em>
                </h2>
                <p className="font-sans text-charcoal/75 text-[16px] md:text-[17px] leading-[1.85] mt-6 max-w-2xl">
                  Every property has opportunities hiding in plain sight. The Revenue Leak Audit helps uncover
                  friction affecting bookings, guest experience, operations, and profitability.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link
                  to="/resources"
                  data-testid="success-cta-resources"
                  className="btn-primary"
                >
                  Get The Revenue Leak Audit <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
